import {
  Dispatch,
  SetStateAction,
  useState,
  useCallback,
  useMemo,
  memo,
} from 'react';
import MenuItem from '@mui/material/MenuItem';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import {
  UserKeys,
  addUserToGroups,
  getIndexedPageGroups,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { AddUserGroup, Group, Page, BigQueryPage } from '../../../../../types';
import GeneralDialog from '../../../../shared/general-dialog';
import {
  GridColDef,
  GridFilterModel,
  GridRowParams,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import StepGroupToolbar from '../../add-user-dialog/form-steps/step-groups-toolbar';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import SharedDataGridServer from '../../../../shared/shared-data-grid-server';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// Memoized role selector component to prevent unnecessary re-renders
const RoleSelector = memo(
  ({
    groupId,
    currentRole,
    onRoleChange,
  }: {
    groupId: string;
    currentRole: string;
    onRoleChange: (groupId: string, newRole: string) => void;
  }) => {
    return (
      <TextField
        slotProps={{
          input: {
            id: `group-role-${groupId}`,
          },
          inputLabel: {
            htmlFor: `group-role-${groupId}`,
          },
        }}
        label="Role"
        select
        value={currentRole}
        onChange={(event) => onRoleChange(groupId, event.target.value)}
        size="small"
        margin="none"
        sx={{ minWidth: 180 }}
      >
        <MenuItem value="MEMBER">Member</MenuItem>
        <MenuItem value="MANAGER">Manager</MenuItem>
        <MenuItem value="OWNER">Owner</MenuItem>
      </TextField>
    );
  }
);

interface AddToGroupProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  currentUserGroups: Page<Group[]> | undefined;
  selectedValues: AddUserGroup[];
  setSelectedValues: Dispatch<SetStateAction<AddUserGroup[]>>;
}

export default function AddToGroupDialogIndexed({
  open,
  handleClose,
  userEmail,
  selectedValues,
  setSelectedValues,
  currentUserGroups,
}: AddToGroupProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const { changeLogRoles } = useUserSettings();
  const [selectedRows, setSelectedRows] = useState<string[]>(() =>
    selectedValues.map((group) => group.groupId)
  );

  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
  }>({
    pageSize: 100,
    page: 0,
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      UserKeys.GroupsIndexedPage,
      paginationModel.page,
      paginationModel.pageSize,
      filterModel,
    ],
    queryFn: () => {
      const object: BigQueryPage<Group[]> = {
        results: [],
        pageSize: paginationModel.pageSize,
        currentPage: paginationModel.page,
        totalCount: 0,
      };

      const sortModel: GridSortModel = [{ field: 'email', sort: 'asc' }];
      return getIndexedPageGroups(object, filterModel, sortModel);
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage:
        'There was an issue loading groups. Please refresh and try again.',
    },
  });

  const { mutate, isPending: isAdding } = useMutation({
    mutationFn: () =>
      addUserToGroups(userEmail, selectedValues, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message:
          'The task to add this user to the selected groups has started.',
      });
      setSelectedValues([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue adding the user to these groups. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_details.groups.create_groups, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, UserRoles.AddGroupMembers.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  // Stable callback for role changes - prevents unnecessary re-renders
  const handleRoleChange = useCallback(
    (groupId: string, newRole: string) => {
      setSelectedValues((prev) =>
        prev.map((group) =>
          group.groupId === groupId
            ? {
                ...group,
                member: {
                  ...group.member,
                  role: newRole as 'MEMBER' | 'MANAGER' | 'OWNER',
                },
              }
            : group
        )
      );
    },
    [setSelectedValues]
  );

  const currentUserGroupEmailSet = useMemo(
    () => new Set(currentUserGroups?.results?.map((g) => g.email) ?? []),
    [currentUserGroups]
  );

  const isAlreadyMember = useCallback(
    (groupEmail: string) => currentUserGroupEmailSet.has(groupEmail),
    [currentUserGroupEmailSet]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'groupEmail',
        headerName: 'Email',
        sortable: false,
        filterable: false,
        display: 'flex',
        flex: 3,
        renderCell: (params) => {
          const group = selectedValues.find((g) => g.groupId === params.value);
          const alreadyMember = isAlreadyMember(params.value);
          const isSelected = Boolean(group);

          return (
            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              sx={{ mr: 4 }}
            >
              <Tooltip
                title={
                  alreadyMember ? 'User is already a member of this group' : ''
                }
                arrow
                placement="top"
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: alreadyMember ? 'text.disabled' : 'text.primary',
                  }}
                >
                  {params.value}
                </Typography>
              </Tooltip>

              {isSelected && group && (
                <RoleSelector
                  groupId={params.value}
                  currentRole={group.member.role}
                  onRoleChange={handleRoleChange}
                />
              )}
            </Stack>
          );
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        display: 'flex',
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const alreadyMember = isAlreadyMember(params.row.groupEmail);
          return (
            <Typography
              variant="body2"
              sx={{
                color: alreadyMember ? 'text.disabled' : 'text.primary',
              }}
            >
              {params.value}
            </Typography>
          );
        },
      },
    ],
    [handleRoleChange, isAlreadyMember, selectedValues]
  );

  const rows =
    data?.results.map((group) => ({
      ...group,
      id: group.email,
      groupEmail: group.email,
      name: group.name,
    })) || [];

  return (
    <GeneralDialog
      open={open}
      handleClose={handleClose}
      title="Add to Group"
      maxWidth="md"
      dialogContent={
        <Box>
          <Typography variant="body2">
            Select any groups you'd like this user to be added to.
          </Typography>
          <Box sx={{ height: 500, mt: 2, p: 2 }}>
            <SharedDataGridServer
              paginationModel={paginationModel}
              onPaginationModelChange={(newModel) => {
                if (isFetching || isLoading) return;

                if (newModel.pageSize !== paginationModel.pageSize) {
                  setPaginationModel({
                    page: 0,
                    pageSize: newModel.pageSize,
                  });
                } else {
                  setPaginationModel({
                    ...newModel,
                  });
                }
              }}
              onFilterModelChange={(model) => {
                const quickFilterValue = model.quickFilterValues?.[0] || '';

                // Remove only the filter we may have added previously
                const otherFilters = model.items.filter(
                  (item) => item.field !== 'email' || item.operator !== 'like'
                );

                if (quickFilterValue) {
                  otherFilters.push({
                    field: 'email',
                    operator: 'like',
                    value: quickFilterValue,
                  });
                }

                setFilterModel({
                  ...model,
                  items: otherFilters,
                });
              }}
              pageSizeOptions={[100]}
              initialState={{
                filter: {
                  filterModel: {
                    items: [],
                  },
                },
              }}
              onRowSelectionModelChange={(newSelection) => {
                const selectedGroupIds = Array.from(
                  newSelection.ids
                ) as string[];
                const newGroups: AddUserGroup[] = selectedGroupIds
                  .map((groupId) => {
                    const existingGroup = selectedValues.find(
                      (group) => group?.groupId === groupId
                    );

                    if (existingGroup) return existingGroup;

                    const row = rows.find((row) => row.id === groupId);

                    if (!row) return null;

                    return {
                      groupId,
                      member: {
                        email: userEmail,
                        role: 'MEMBER',
                        canActOn: false,
                      },
                    };
                  })
                  .filter(Boolean) as AddUserGroup[];

                setSelectedValues(newGroups);
              }}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              slots={{
                toolbar: StepGroupToolbar,
              }}
              checkboxSelection
              disableColumnMenu
              keepNonExistentRowsSelected
              isRowSelectable={(params: GridRowParams) =>
                !isAlreadyMember(params.row.groupEmail)
              }
              columns={columns}
              data={data}
              rows={rows || []}
              loading={isLoading || isFetching}
              sx={{
                minWidth: 600,
              }}
            />
          </Box>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isAdding}
          />
        </Box>
      }
      primaryButton={{
        loading: isAdding,
        action: handleSubmit,
        disabled: selectedValues.length === 0,
        title: 'Add',
      }}
    />
  );
}
